import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, ArrowUpDown, Smartphone, Copy, Send } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RestPlanner } from "@/components/RestPlanner";
import { toast } from "sonner";
import { QUALIFICATIONS, DRIVER_STATUS } from "@/lib/constants";

const EMPTY = { nom: "", prenom: "", qualifications: [], depot: "Siège", bases: [], max_hours: 48, status: "disponible", email: "", password: "", prefer_rest: false, rest_dates: [] };

export default function Drivers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [shareCreds, setShareCreds] = useState(null);
  const [restDate, setRestDate] = useState("");
  const [depots, setDepots] = useState([]);
  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const set = new Set(["Siège"]);
      (r.data || []).forEach((v) => v.depot && set.add(v.depot));
      setDepots([...set]);
    }).catch(() => {});
  }, []);

  const toggleBase = (b) => setForm((f) => {
    const has = (f.bases || []).includes(b);
    return { ...f, bases: has ? f.bases.filter((x) => x !== b) : [...(f.bases || []), b] };
  });
  const addRestDate = () => {
    if (!restDate) return;
    setForm((f) => f.rest_dates?.includes(restDate) ? f : { ...f, rest_dates: [...(f.rest_dates || []), restDate].sort() });
    setRestDate("");
  };
  const removeRestDate = (d) => setForm((f) => ({ ...f, rest_dates: (f.rest_dates || []).filter((x) => x !== d) }));

  const load = () => api.get("/drivers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Ouverture directe de la fiche via lien cliquable (?focus=<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus && items.length) {
      const d = items.find((x) => x.id === focus);
      if (d) { openEdit(d); setSearchParams({}, { replace: true }); }
    }
    /* eslint-disable-next-line */
  }, [items, searchParams]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d, email: d.account_email || "", password: "" }); setEditId(d.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleQual = (q) => {
    setForm((f) => {
      const has = f.qualifications.includes(q);
      const qualifications = has ? f.qualifications.filter((x) => x !== q) : [...f.qualifications, q];
      return { ...f, qualifications, max_hours: qualifications.includes("Apprenti") ? 35 : 48 };
    });
  };

  const save = async () => {
    try {
      const loginSet = !!(form.email && form.password);
      if (editId) await api.put(`/drivers/${editId}`, form);
      else await api.post("/drivers", form);
      toast.success("Conducteur enregistré");
      setOpen(false); load();
      if (loginSet) setShareCreds({ email: form.email });
    } catch (err) { toast.error(apiError(err)); }
  };

  const shareLink = shareCreds ? `${window.location.origin}/login?email=${encodeURIComponent(shareCreds.email)}` : "";

  const remove = async (id) => { await api.delete(`/drivers/${id}`); load(); };
  const statusLabel = (s) => DRIVER_STATUS.find((x) => x.value === s)?.label || s;

  const [sort, setSort] = useState({ key: null, dir: 1 });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const sortVal = (d, key) => {
    if (key === "qualifications") return (d.qualifications || []).slice().sort().join(",");
    if (key === "status") return statusLabel(d.status);
    return d[key] || "";
  };
  const sorted = sort.key
    ? [...items].sort((a, b) => sortVal(a, sort.key).localeCompare(sortVal(b, sort.key), "fr") * sort.dir)
    : items;

  const SortHead = ({ k, children }) => (
    <TableHead>
      <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`drivers-sort-${k}`}>
        {children}
        {sort.key === k ? (sort.dir === 1 ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Conducteurs</h1>
          <p className="text-muted-foreground text-sm">Qualifications, dépôts & règles d'équipages.</p>
        </div>
        <Button onClick={openNew} data-testid="drivers-add-button" className="gap-2"><Plus className="h-4 w-4" /> Ajouter</Button>
      </div>

      <Tabs defaultValue="liste">
        <TabsList>
          <TabsTrigger value="liste" data-testid="drivers-tab-liste">Liste</TabsTrigger>
          <TabsTrigger value="repos" data-testid="drivers-tab-repos">Planning des repos</TabsTrigger>
        </TabsList>

        <TabsContent value="liste" className="mt-4">
          <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="drivers-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <SortHead k="qualifications">Qualifications</SortHead>
              <SortHead k="depot">Dépôt</SortHead>
              <TableHead>Max h/sem</TableHead>
              <SortHead k="status">Statut</SortHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucun conducteur.</TableCell></TableRow>}
            {sorted.map((d) => (
              <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
                <TableCell className="font-medium">
                  {d.prenom} {d.nom}
                  {d.has_account && <Badge variant="outline" className="ml-2 border-green-500 text-green-600 text-[10px]">Login ✓</Badge>}
                </TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  {d.qualifications.map((q) => (
                    <Badge key={q} variant="outline" className={q === "DEA" ? "border-primary text-primary" : q === "Apprenti" ? "border-accent text-amber-700 dark:text-amber-300 bg-accent/20" : ""}>{q}</Badge>
                  ))}
                </TableCell>
                <TableCell className="text-sm">{d.depot}</TableCell>
                <TableCell className="text-sm">{d.max_hours} h</TableCell>
                <TableCell><Badge variant="outline" className={d.status === "disponible" ? "border-green-500 text-green-600" : "opacity-70"}>{statusLabel(d.status)}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`driver-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(d.id)} data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="repos" className="mt-4">
          <RestPlanner drivers={sorted} onChange={load} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[88vh] flex flex-col">
          <DialogHeader className="shrink-0"><DialogTitle>{editId ? "Modifier" : "Nouveau"} conducteur</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Formulaire de saisie des informations du conducteur.</DialogDescription>
          <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0 pr-1 pb-1" data-testid="driver-form-scroll">
            <div><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} className="mt-1" data-testid="driver-prenom" /></div>
            <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1" data-testid="driver-nom" /></div>
            <div className="col-span-2">
              <Label>Qualifications</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {QUALIFICATIONS.map((q) => (
                  <label key={q} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.qualifications.includes(q)} onCheckedChange={() => toggleQual(q)} data-testid={`driver-qual-${q}`} />{q}
                  </label>
                ))}
              </div>
              {form.qualifications.includes("Apprenti") && <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">Apprenti : plafonné à 35 h/sem, pas de trajets taxi/tourisme.</p>}
            </div>
            <div><Label>Dépôt (base principale)</Label><Input value={form.depot} onChange={(e) => set("depot", e.target.value)} className="mt-1" data-testid="driver-depot" /></div>
            <div className="sm:col-span-2">
              <Label>Bases de départ autorisées</Label>
              <p className="text-xs text-muted-foreground mb-1.5">Le conducteur ne peut prendre que des véhicules basés sur l'une de ces adresses. La base de départ = base de retour du véhicule.</p>
              <div className="flex flex-wrap gap-3" data-testid="driver-bases">
                {[...new Set([form.depot, ...depots].filter(Boolean))].map((b) => (
                  <label key={b} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox checked={(form.bases || []).includes(b)} onCheckedChange={() => toggleBase(b)} data-testid={`driver-base-${b}`} />{b}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Si aucune case cochée, le dépôt principal est utilisé par défaut.</p>
            </div>
            <div>
              <Label>Amplitude horaire (h/sem)</Label>
              <Input type="number" min={1} max={60} value={form.max_hours}
                onChange={(e) => set("max_hours", Number(e.target.value) || 0)}
                disabled={form.qualifications.includes("Apprenti")}
                className="mt-1" data-testid="driver-max-hours" />
              {form.qualifications.includes("Apprenti")
                ? <p className="text-[11px] text-muted-foreground mt-1">Apprenti : plafonné à 35 h/sem.</p>
                : <p className="text-[11px] text-muted-foreground mt-1">Amplitude hebdomadaire maximale.</p>}
            </div>
            <div className="col-span-2">
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="driver-status"><SelectValue /></SelectTrigger>
                <SelectContent>{DRIVER_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="col-span-2 border-t border-border pt-3 mt-1">
              <Label className="text-sm font-semibold">Accès à l'application chauffeur</Label>
              <p className="text-[11px] text-muted-foreground mb-2">
                {editId && form.email
                  ? "Un login existe. Renseignez un nouveau mot de passe pour le réinitialiser."
                  : "Créez un login (email + mot de passe) pour que le chauffeur se connecte à l'app."}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Email de connexion</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="chauffeur@…" className="mt-1" data-testid="driver-login-email" /></div>
                <div><Label className="text-xs">Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••" className="mt-1" data-testid="driver-login-password" /></div>
              </div>
            </div>

            <div className="col-span-2 border-t border-border pt-3">
              <Label className="text-sm font-semibold">Repos du chauffeur</Label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={!!form.prefer_rest} onCheckedChange={(v) => set("prefer_rest", !!v)} data-testid="driver-prefer-rest" />
                <span>À mettre en repos en priorité <span className="block text-xs text-muted-foreground">La régul ne l'utilise qu'en dernier recours (sans dépasser l'amplitude).</span></span>
              </label>
              <div className="mt-3">
                <Label className="text-xs">Journées de repos (la régul l'exclut ces dates)</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="date" value={restDate} onChange={(e) => setRestDate(e.target.value)} data-testid="driver-rest-date" />
                  <Button type="button" variant="outline" onClick={addRestDate} data-testid="driver-rest-add">Ajouter</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(form.rest_dates || []).map((d) => (
                    <Badge key={d} variant="secondary" className="gap-1" data-testid={`driver-rest-${d}`}>
                      {new Date(d).toLocaleDateString("fr-FR")}
                      <button type="button" onClick={() => removeRestDate(d)} className="ml-0.5 hover:text-destructive" aria-label="retirer">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-border pt-3 bg-background">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="driver-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareCreds} onOpenChange={(o) => !o && setShareCreds(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Login chauffeur prêt</DialogTitle></DialogHeader>
          {shareCreds && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Envoyez ce lien (ou le QR) au chauffeur. Son email est pré-rempli ; communiquez-lui le mot de passe que vous avez défini.</p>
              <div className="rounded-md bg-secondary px-3 py-2 text-sm"><span className="text-muted-foreground">Email :</span> <span className="font-medium">{shareCreds.email}</span></div>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(shareLink)}`} alt="QR connexion chauffeur" width={150} height={150} className="mx-auto rounded-lg border border-border" data-testid="driver-share-qr" />
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-secondary px-3 py-2 text-xs">{shareLink}</code>
                <Button variant="outline" size="icon" data-testid="driver-share-copy"
                  onClick={async () => { try { await navigator.clipboard.writeText(shareLink); toast.success("Lien copié"); } catch { toast.info(shareLink); } }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button className="w-full gap-2" onClick={() => setShareCreds(null)} data-testid="driver-share-done"><Send className="h-4 w-4" /> Terminé</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
